import React, { useCallback, useEffect, useState } from "react";
import { VehiclePostings } from "./VehiclePostings";
import { Assumptions } from "./Assumptions";
import { Button, Bars, Guide, Kpis, SectionHeading, useEconomyText } from "./shared";
import { bookedComparableTotal, comparableVehicleCategories, estimatedOperatingCost, vehicleCostDifference } from "./vehicleCostTotals";

const comparableCategories = comparableVehicleCategories;

export function Vehicles({ vehicles, year, month, refresh, onEditingChange, controls }) {
  const { t, money, monthName } = useEconomyText();
  const [selected, setSelected] = useState("");
  const [editing, setEditing] = useState(false);
  const [bookedByVehicle, setBookedByVehicle] = useState({});
  const vehicle = vehicles.find((row) => String(row.id) === selected) || vehicles[0];
  const vehicleId = vehicle?.id;
  const periodKey = `${year}-${month}`;

  useEffect(() => {
    setBookedByVehicle({});
  }, [periodKey]);

  const onComparableTotal = useCallback((total) => {
    if (vehicleId == null) return;
    setBookedByVehicle((old) => {
      const prev = old[vehicleId];
      if (prev && prev.periodKey === periodKey && prev.total === total) return old;
      return { ...old, [vehicleId]: { periodKey, total } };
    });
  }, [vehicleId, periodKey]);

  if (!vehicle) return <section className="eco-stack"><SectionHeading title={t("vehiclesTitle")} description={t("vehicleIntro")} controls={controls} /><p className="eco-empty">{t("noVehicles")}</p></section>;
  const estimate = vehicle.estimate;
  const cardMetrics = (row) => {
    const calc = estimatedOperatingCost(row.estimate);
    const entry = bookedByVehicle[row.id];
    const booked = entry && entry.periodKey === periodKey ? entry.total : null;
    return { calc, booked, diff: vehicleCostDifference(calc, booked) };
  };
  return <section className="eco-stack">
    <SectionHeading title={t("vehiclesTitle")} description={t("vehicleIntro")} controls={controls}><Button disabled={editing} onClick={() => { setEditing(true); onEditingChange?.(true); }}>{t("settings")}</Button></SectionHeading>
    <div className="eco-vehicle-cards">{vehicles.map((row) => {
      const metrics = cardMetrics(row);
      const km = row.months.find((entry) => entry.month === month)?.kilometers;
      return <button type="button" className="eco-vehicle-card" key={row.id} aria-pressed={vehicle.id === row.id} disabled={editing} onClick={() => setSelected(String(row.id))}>
        <span><span><strong>{`${row.make || ""} ${row.model || ""}`.trim() || row.number_plate}</strong><span className="eco-muted">{[row.fuel_type, km != null ? `${money(km)} km` : null].filter(Boolean).join(" · ")}</span></span><span className="eco-plate">{row.number_plate}</span></span>
        <span className="eco-vehicle-facts">
          <span><span className="eco-label">{t("calculated")}</span><strong>{money(metrics.calc)}</strong></span>
          <span><span className="eco-label">{t("booked")}</span><strong>{money(metrics.booked)}</strong></span>
          <span><span className="eco-label">{t("difference")}</span><strong className={metrics.diff == null ? undefined : metrics.diff > 0 ? "eco-warning" : undefined}>{money(metrics.diff)}</strong></span>
        </span>
      </button>;
    })}</div>
    <Guide>{t("vehicleHelp")}</Guide>
    {editing && <Assumptions key={vehicle.id} settingKey={`vehicle-${vehicle.id}`} onSaved={refresh} onClose={() => { setEditing(false); onEditingChange?.(false); }} />}
    <Kpis values={{ kilometers: vehicle.months.find((row) => row.month === month)?.kilometers, operating_cost: estimatedOperatingCost(estimate), cash_out: estimate?.available ? estimate.cash_out : null, cost_per_km: estimate?.cost_per_km }} />
    {!estimate?.available && <p className="eco-warning">{t("missing")}</p>}
    {estimate?.estimated_excess_km != null && <Kpis values={{ estimated_annual_km: estimate.estimated_annual_km, estimated_excess_km: estimate.estimated_excess_km, estimated_excess_cost: estimate.estimated_excess_cost }} />}
    <div className="eco-detail-grid"><VehiclePostings key={`${vehicle.id}-${year}-${month}`} vehicle={vehicle} year={year} month={month} onComparableTotal={onComparableTotal} comparableCategories={comparableCategories} />
    <div className="eco-card"><h3>{t("kilometers")}</h3><Bars rows={vehicle.months.map((row) => ({ label: monthName(row.month), value: row.kilometers }))} /></div></div>
  </section>;
}
